import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, ChevronRightIcon, DownloadIcon } from 'lucide-react';
import { GET_STARTED_URL, ENTERPRISE_DEMO_URL, SAMPLE_MEMO_DOWNLOAD_URL, SAMPLE_MEMO_PATH } from '../data/dgpt';
import { MEMO_EXPLAINER, SAMPLE_MEMO } from '../data/sampleMemo';
import { usePageMeta, canonicalUrl } from '../hooks/usePageMeta';
import { MemoDocument } from '../components/memo/MemoDocument';
import { CtaButton } from '../components/ui/CtaButton';
import { Eyebrow } from '../components/ui/Eyebrow';
export function SampleMemo() {
  usePageMeta({
    title: 'Sample AI Investment Memo | DiligenceGPT™',
    description: 'Read a sample investment memo prepared with DiligenceGPT™: thesis fit by criterion, sourced strengths and risks, open questions and a financial snapshot. Fictional company, no email required.',
    path: SAMPLE_MEMO_PATH,
    keywords: ['AI investment memo generator', 'sample investment memo', 'investment committee memo'],
    jsonLd: [{
      '@type': 'BreadcrumbList',
      itemListElement: [{
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: canonicalUrl('/')
      }, {
        '@type': 'ListItem',
        position: 2,
        name: 'Sample Memo',
        item: canonicalUrl(SAMPLE_MEMO_PATH)
      }]
    }]
  });
  return <main id="main" className="bg-ink" data-id="element-584" data-mp="o3tta6k1jmo6">
      <section aria-labelledby="memo-page-heading" className="border-b border-hairline-soft" data-id="element-585" data-mp="o4v7gjwcspa4">
        <div className="mx-auto w-full max-w-[1100px] px-4 pb-10 pt-10 sm:px-6 lg:px-8 lg:pt-14" data-id="element-586" data-mp="t2dj4i1r9ilt">
          <nav aria-label="Breadcrumb" data-id="element-587" data-mp="xzww7empvpc6">
            <ol className="flex items-center gap-1.5 text-xs text-text-tertiary" data-id="element-588" data-mp="123l5c1qzyszj">
              <li data-id="element-589" data-mp="121iczb2dgnrn">
                <Link to="/" className="hover:text-text-primary" data-id="element-590" data-mp="17268oa1jpss7">Home</Link>
              </li>
              <li aria-hidden="true" data-id="element-591" data-mp="33uy8omdbgsth"><ChevronRightIcon className="h-3 w-3" data-id="element-592" data-mp="33vhs67im7p9m" /></li>
              <li aria-current="page" className="text-text-secondary" data-id="element-593" data-mp="33x2ljtt7125w">Sample Memo</li>
            </ol>
          </nav>
          <div className="mt-6" data-id="element-594" data-mp="fz14vjrlbpp5"><Eyebrow data-id="element-595" data-mp="fyj3k8q2mlad">Sample output</Eyebrow></div>
          <h1 id="memo-page-heading" className="mt-4 text-balance text-[38px] font-semibold leading-[1.04] tracking-[-0.035em] text-text-primary sm:text-[52px]" data-id="element-596" data-mp="g1f1hx1x59yt">
            Sample investment memo
          </h1>
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-text-secondary sm:text-lg" data-id="element-597" data-mp="oxmo7h4vz8qz">
            {SAMPLE_MEMO.label}. This shows the structure your team receives: thesis fit by criterion, sourced findings, open
            questions and a financial snapshot — a draft for analyst review, not a recommendation.
          </p>
          <p className="mt-2 text-sm text-text-tertiary" data-id="element-598" data-mp="17qqin4pxhpmn">{SAMPLE_MEMO.preparedFor}</p>

          <dl className="mt-8 grid gap-3 sm:grid-cols-3" data-id="element-599" data-mp="13odegkqtxzrz">
            {MEMO_EXPLAINER.map(item => <div key={item.title} className="rounded-xl border border-hairline bg-panel p-4" data-id="element-600" data-mp="1hnv6ra3c3v63">
                <dt className="text-sm font-semibold text-text-primary" data-id="element-601" data-mp="1cq3cwaj4pyz9">{item.title}</dt>
                <dd className="mt-1 text-[13px] leading-relaxed text-text-secondary" data-id="element-602" data-mp="1wbmwdhn346rc">{item.body}</dd>
              </div>)}
          </dl>
        </div>
      </section>

      <section aria-label="Memo" className="border-b border-hairline-soft bg-surface" data-id="element-603" data-mp="2183t308cntg1">
        <div className="mx-auto w-full max-w-[1100px] px-4 py-12 sm:px-6 lg:px-8 lg:py-16" data-id="element-604" data-mp="1mfa0qe7zbity">
          <MemoDocument variant="full" titleAs="h2" data-id="element-605" data-mp="1red845bfm153" />
          {SAMPLE_MEMO_DOWNLOAD_URL ? <div className="mt-6" data-id="element-606" data-mp="1djklkmx4jkp0">
              <CtaButton href={SAMPLE_MEMO_DOWNLOAD_URL} event="sample_memo_download" eventParams={{
            placement: 'sample_page'
          }} variant="outline" data-id="element-607" data-mp="1ibc367yayg30">
                <DownloadIcon className="h-4 w-4" aria-hidden="true" data-id="element-608" data-mp="13o96g3ht0bgq" />
                Download PDF
              </CtaButton>
            </div> : null}
        </div>
      </section>

      <section aria-labelledby="memo-cta-heading" data-id="element-609" data-mp="unawz5oljbqr">
        <div className="mx-auto flex w-full max-w-[1100px] flex-col items-start gap-6 px-4 py-16 sm:px-6 md:flex-row md:items-center md:justify-between lg:px-8" data-id="element-610" data-mp="ynld3b63rijc">
          <div data-id="element-611" data-mp="rjn0ng0zvcmt">
            <h2 id="memo-cta-heading" className="text-2xl font-semibold tracking-[-0.02em] text-text-primary" data-id="element-612" data-mp="rmhnfe7hd9je">
              Run this on your next pitch deck.
            </h2>
            <p className="mt-1 text-sm text-text-secondary" data-id="element-613" data-mp="ht0ro4ns9kd3">Start with one assessment, or discuss a workflow for your team.</p>
          </div>
          <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row" data-id="element-614" data-mp="8tx3im9reerq">
            <CtaButton href={GET_STARTED_URL} event="primary_cta_click" eventParams={{
            placement: 'sample_page',
            cta: 'try_pitch_deck_screener'
          }} size="lg" data-id="element-615" data-mp="8ru9zs9ee3i9">
              Try Pitch Deck Screener
              <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-616" data-mp="3v37xc466o6qd" />
            </CtaButton>
            <CtaButton href={ENTERPRISE_DEMO_URL} event="enterprise_demo_click" eventParams={{
            placement: 'sample_page'
          }} variant="outline" size="lg" data-id="element-617" data-mp="3q4519986lauj">
              Book an Enterprise Demo
            </CtaButton>
          </div>
        </div>
      </section>
    </main>;
}